import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Skeleton
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SecurityIcon from '@mui/icons-material/Security'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { getCurrentUser } from '../../../auth/ducks/selectors'
import APIService from '../../../../services/APIService'
import DomainListField, { domainListError, serverDomainError } from './DomainListField'
import ApproverPicker from 'components/ApproverPicker'
import organisationService from 'services/organisationService'

const ACCESS_OPTIONS = [
  { value: 'domain', title: 'People from these email domains', description: 'Only people on this Building Passport whose email address is at one of the domains below.' },
  { value: 'private', title: 'Only me', description: 'Nobody else can see the files unless you share them.' },
  { value: 'public', title: 'Everyone on this Building Passport', description: 'Anyone with access to this Building Passport can see the files.' }
]

const SYNC_OPTIONS = [
  { value: 'read_write', title: 'Read and write (recommended)' },
  { value: 'read_only', title: 'Read only' }
]

const SharePointAccessControlModal = ({
  open,
  onClose,
  customView,
  buildingPassportId,
  onSuccess,
  onReauthRequired
}) => {
  const api = APIService()
  const currentUser = useSelector(getCurrentUser)
  const [loading, setLoading] = useState(false)
  const [syncModeLoading, setSyncModeLoading] = useState(false)
  const [error, setError] = useState(null)
  const [domainError, setDomainError] = useState(null)
  const [accessType, setAccessType] = useState('')
  const [domainRestriction, setDomainRestriction] = useState('')
  const [userDomain, setUserDomain] = useState('')
  const [syncMode, setSyncMode] = useState('read_write')
  const [isInitialized, setIsInitialized] = useState(false)
  const [approverIds, setApproverIds] = useState([])
  const [approverInitial, setApproverInitial] = useState([])
  const [approverData, setApproverData] = useState(null)
  const [approverStatus, setApproverStatus] = useState('loading')
  const [approverNote, setApproverNote] = useState('')
  const [approverReload, setApproverReload] = useState(0)

  useEffect(() => {
    if (!open) {
      setAccessType('')
      setDomainRestriction('')
      setUserDomain('')
      setSyncMode('read_write')
      setIsInitialized(false)
      setError(null)
      setDomainError(null)
      return
    }

    const domain = currentUser?.email ? currentUser.email.split('@')[1] : ''
    setUserDomain(domain)

    if (customView) {
      const cvAccessType = customView.accessType || 'domain'
      const stored = customView.domainRestriction || (cvAccessType === 'domain' ? domain : '')
      setAccessType(cvAccessType)
      setDomainRestriction(stored ? stored.split(';').map(d => `@${d}`).join('; ') : '')
      setSyncMode(customView.sharepointSyncMode || 'read_write')
      setIsInitialized(true)
    }
  }, [open, customView, currentUser])

  useEffect(() => {
    if (!open || !customView?.id) return undefined
    let cancelled = false
    setApproverStatus('loading')
    setApproverData(null)
    setApproverNote('')
    organisationService.getSharePointApprovers({ buildingPassportId, customViewId: customView.id })
      .then(res => {
        if (cancelled) return
        const data = res?.data || {}
        const ids = (data.approvers || []).map(a => a.id)
        setApproverData(data)
        setApproverIds(ids)
        setApproverInitial(ids)
        setApproverStatus('ready')
      })
      .catch(err => {
        if (cancelled) return
        if (err?.response?.status === 403) {
          setApproverNote(err?.response?.data?.message || 'Only an Admin of this building or a Super Admin of its organisation can choose who approves requests.')
          setApproverStatus('forbidden')
        } else {
          setApproverStatus('error')
        }
      })
    return () => { cancelled = true }
  }, [open, customView?.id, buildingPassportId, approverReload])

  const approverPeople = useMemo(() => {
    const known = new Map()
    ;(approverData?.candidates || []).forEach(c => known.set(String(c.id), c))
    ;(approverData?.approvers || []).forEach(a => { if (!known.has(String(a.id))) known.set(String(a.id), a) })
    return [...known.values()]
  }, [approverData])

  const approversChanged = [...approverIds].sort().join(',') !== [...approverInitial].sort().join(',')

  const handleSave = async () => {
    setLoading(true)
    setError(null)
    setDomainError(null)

    try {
      if (approversChanged && approverStatus === 'ready') {
        try {
          await organisationService.setSharePointApprovers({ buildingPassportId, customViewId: customView.id, userIds: approverIds })
        } catch (approverError) {
          const errors = approverError?.response?.data?.errors || {}
          setError(errors.userIds?.[0] || approverError?.response?.data?.message || 'We could not save the approvers, so nothing was changed. Please try again.')
          setLoading(false)
          return
        }
        setApproverInitial(approverIds)
      }
      const response = await api.put(
        `/building-passport/${buildingPassportId}/custom-views/${customView.id}/sharepoint-access`,
        {
          access_type: accessType,
          domain_restriction: accessType === 'domain' ? (domainRestriction || userDomain) : null
        }
      )

      const data = response.data?.data || response.data

      if (data) {
        if (onSuccess) {
          onSuccess(data.customView)
        }
        onClose()
      } else {
        setError('The changes could not be saved. Please try again.')
      }
    } catch (err) {
      const fieldError = serverDomainError(err)
      if (fieldError) {
        setDomainError(fieldError)
      } else {
        setError('The changes could not be saved: ' + (err.response?.data?.message || err.message))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSyncModeChange = async newMode => {
    if (newMode === syncMode) return

    setSyncModeLoading(true)
    setError(null)

    try {
      const response = await api.put(
        `/building-passport/${buildingPassportId}/custom-views/${customView.id}/sharepoint-sync-mode`,
        { sync_mode: newMode }
      )

      const data = response.data?.data || response.data

      if (data) {
        setSyncMode(newMode)

        if (data.reauthRequired && onReauthRequired) {
          onClose()
          onReauthRequired(customView.id)
        }
      } else {
        setError('The sync setting could not be changed. Please try again.')
      }
    } catch (err) {
      setError('The sync setting could not be changed: ' + (err.response?.data?.message || err.message))
    } finally {
      setSyncModeLoading(false)
    }
  }

  const domainInvalid = accessType === 'domain' && !!domainListError(domainRestriction)

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <Header>
        <Box display='flex' alignItems='center' gap={1.5} minWidth={0}>
          <SecurityIcon color='primary' />
          <Box minWidth={0}>
            <Typography variant='h6' sx={{ lineHeight: 1.3 }}>
              Who can see these files
            </Typography>
            <Typography variant='body2' color='text.secondary' noWrap>
              SharePoint folder: {customView?.name}
            </Typography>
          </Box>
        </Box>
        <Tooltip title='Close'>
          <IconButton onClick={onClose} aria-label='Close'>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Header>

      <DialogContent sx={{ pt: 1, pb: 1 }}>
        {error && (
          <Alert severity='error' sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          Changes apply straight away to every file already synced from this folder, and to new ones.
        </Typography>

        {!isInitialized ? (
          <Box display='flex' flexDirection='column' gap={1}>
            {[0, 1, 2].map(i => (
              <Skeleton key={i} variant='rounded' height={56} animation='pulse' />
            ))}
          </Box>
        ) : (
          <RadioGroup value={accessType} onChange={e => setAccessType(e.target.value)} sx={{ gap: 1 }}>
            {ACCESS_OPTIONS.map(option => (
              <Box
                key={option.value}
                sx={{
                  border: 1,
                  borderColor: accessType === option.value ? 'primary.main' : 'divider',
                  bgcolor: accessType === option.value ? 'action.hover' : 'transparent',
                  borderRadius: 2,
                  pl: 0.5,
                  pr: 1,
                  py: 0.25,
                  transition: 'border-color 0.15s ease, background-color 0.15s ease'
                }}
              >
                <FormControlLabel
                  value={option.value}
                  control={<Radio size='small' />}
                  sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                  label={
                    <Box sx={{ pt: 0.75 }}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                        {option.title}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {option.description}
                      </Typography>
                    </Box>
                  }
                />
                {option.value === 'domain' && accessType === 'domain' && (
                  <Box sx={{ pl: 4.5, pr: 1, pb: 1.5, pt: 0.5 }}>
                    <DomainListField
                      value={domainRestriction}
                      onChange={value => { setDomainRestriction(value); setDomainError(null) }}
                      userDomain={userDomain}
                      enabled={open}
                      serverError={domainError}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </RadioGroup>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            Who approves access requests for these documents?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            An approver decides whether someone can open a locked document. Requests for SharePoint documents go to
            these people, not to the person who connected SharePoint. Leave this empty and your organisation&apos;s
            Super Admins decide, shown as &quot;Super Admins (default)&quot;.
          </Typography>
          {approverStatus === 'loading' && <Skeleton variant='rounded' height={44} />}
          {approverStatus === 'ready' && !approverData?.clientOrganisation && (
            <Typography variant='body2'>{approverData?.decidedByText}</Typography>
          )}
          {approverStatus === 'ready' && approverData?.clientOrganisation && (
            <>
              <ApproverPicker value={approverIds} onChange={setApproverIds} colleagues={approverPeople} />
              {approverData?.decidedByText && (
                <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  {approverData.decidedByText}
                </Typography>
              )}
            </>
          )}
          {approverStatus === 'forbidden' && (
            <Typography variant='body2'>{approverNote}</Typography>
          )}
          {approverStatus === 'error' && (
            <Box display='flex' alignItems='center' flexWrap='wrap' gap={1.5}>
              <Typography variant='body2'>
                We couldn&apos;t load who approves requests. Please try again.
              </Typography>
              <Button variant='outlined' sx={{ minHeight: 40 }} onClick={() => setApproverReload(n => n + 1)}>Try again</Button>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            Sync
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
            Saved as soon as you choose. Changing it asks you to sign in to Microsoft again.
          </Typography>
          <RadioGroup
            row
            value={syncMode}
            onChange={e => handleSyncModeChange(e.target.value)}
            sx={{ columnGap: 3 }}
          >
            {SYNC_OPTIONS.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                disabled={syncModeLoading}
                control={<Radio size='small' />}
                label={<Typography variant='body2'>{option.title}</Typography>}
                sx={{ minHeight: 40 }}
              />
            ))}
          </RadioGroup>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSave} disabled={loading || !isInitialized || domainInvalid}>
          {loading ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}

export default SharePointAccessControlModal

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    margin: 16px;
    width: calc(100% - 32px);
  }

  @media (max-width: 768px) {
    .MuiDialog-container {
      align-items: flex-start;
    }

    .MuiDialog-paper {
      max-height: calc(100% - 112px);
    }
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 8px 24px;
`
